import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { entityClient } from '../services/entityClient';
import { Plus, Trash2, X, Edit2, Building2, Phone, Mail, MapPin, Eye, Upload, FileText, Download, DollarSign, Award } from 'lucide-react';

const Suppliers = ({ user }) => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showQuotesModal, setShowQuotesModal] = useState(null); // Supplier ID for quotes modal
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [quoteTitle, setQuoteTitle] = useState('');
  
  // Only Admin can delete suppliers
  const canDelete = user?.role === 'admin';
  
  const [formData, setFormData] = useState({
    name_arabic: '',
    name_english: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    cr_number: '',
    vat_number: '',
    contact_person: '',
    bank_name: '',
    bank_account: '',
    bank_iban: '',
    notes: '',
    materials: ''
  });

  useEffect(() => {
    fetchSuppliers();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchSuppliers();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axiosInstance.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch suppliers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name_arabic || !formData.name_english || !formData.email) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      if (editingSupplier) {
        await axiosInstance.put(`/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated successfully');
      } else {
        const response = await axiosInstance.post('/suppliers', formData);
        if (response.data.approval_status === 'pending') {
          toast.success('Supplier submitted for approval', {
            description: 'Waiting for admin approval before it appears in the list'
          });
        } else {
          toast.success('Supplier created successfully');
        }
      }
      setShowModal(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name_arabic: supplier.name_arabic || '',
      name_english: supplier.name_english || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      mobile: supplier.mobile || '',
      address: supplier.address || '',
      cr_number: supplier.cr_number || '',
      vat_number: supplier.vat_number || '',
      contact_person: supplier.contact_person || '',
      bank_name: supplier.bank_name || '',
      bank_account: supplier.bank_account || '',
      bank_iban: supplier.bank_iban || '',
      notes: supplier.notes || '',
      materials: supplier.materials || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (supplier) => {
    const id = typeof supplier === 'string' ? supplier : supplier.id;
    const displayName = typeof supplier === 'object'
      ? (supplier.name_english || supplier.name_arabic || supplier.name || supplier.id)
      : undefined;
    const res = await entityClient.delete('supplier', id, { displayName });
    if (res.mode === 'deleted') { toast.success('تم حذف المورّد'); fetchSuppliers(); }
    else if (res.mode === 'archived') { fetchSuppliers(); }
    else if (res.mode === 'error') { toast.error('فشل الحذف: ' + (res.error?.response?.data?.detail || res.error?.message || '')); }
  };

  const resetForm = () => {
    setFormData({
      name_arabic: '',
      name_english: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      cr_number: '',
      vat_number: '',
      contact_person: '',
      bank_name: '',
      bank_account: '',
      bank_iban: '',
      notes: '',
      materials: ''
    });
    setEditingSupplier(null);
  };

  // Upload price quote for supplier
  const handleUploadQuote = async (supplierId, file) => {
    if (!file || !quoteTitle.trim()) {
      toast.error('Please enter a title for the quote');
      return;
    }
    
    try {
      setUploadingQuote(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('title', quoteTitle);
      
      await axiosInstance.post(`/suppliers/${supplierId}/upload-quote`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Price quote uploaded successfully');
      setQuoteTitle('');
      fetchSuppliers(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload quote');
    } finally {
      setUploadingQuote(false);
    }
  };

  // Delete price quote
  const handleDeleteQuote = async (supplierId, quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      await axiosInstance.delete(`/suppliers/${supplierId}/quote/${quoteId}`);
      toast.success('Quote deleted successfully');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return <div className="text-center py-12">Loading suppliers...</div>;
  }

  return (
    <div data-testid="suppliers-page">
      <div className="page-header">
        <h2 className="page-title">Suppliers</h2>
        <button
          data-testid="add-supplier-button"
          onClick={openNewModal}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Supplier
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Building2 className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Suppliers</h3>
          <p className="text-3xl font-bold text-gray-900">{suppliers.length}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <Building2 className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Active</h3>
          <p className="text-3xl font-bold text-green-600">
            {suppliers.filter(s => s.status === 'active').length}
          </p>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <Building2 className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Inactive</h3>
          <p className="text-3xl font-bold text-red-600">
            {suppliers.filter(s => s.status === 'inactive').length}
          </p>
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {suppliers.length > 0 ? (
          suppliers.map((supplier) => (
            <div 
              key={supplier.id} 
              data-testid={`supplier-card-${supplier.id}`}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-xs text-blue-600 font-medium mb-1">{supplier.supplier_number}</p>
                  <h3 className="font-bold text-gray-900 text-lg">{supplier.name_english}</h3>
                  <p className="text-gray-600 text-sm" dir="rtl">{supplier.name_arabic}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  supplier.status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {supplier.status}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {supplier.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{supplier.email}</span>
                  </div>
                )}
                {(supplier.phone || supplier.mobile) && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{supplier.phone || supplier.mobile}</span>
                  </div>
                )}
                {supplier.address && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{supplier.address}</span>
                  </div>
                )}
              </div>

              {(supplier.cr_number || supplier.vat_number) && (
                <div className="border-t pt-3 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {supplier.cr_number && (
                      <div>
                        <span className="text-gray-500">CR:</span>
                        <span className="ml-1 font-medium">{supplier.cr_number}</span>
                      </div>
                    )}
                    {supplier.vat_number && (
                      <div>
                        <span className="text-gray-500">VAT:</span>
                        <span className="ml-1 font-medium">{supplier.vat_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}`)}
                  className="flex-1 btn-primary text-sm py-2"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  View
                </button>
                <button
                  onClick={() => handleEdit(supplier)}
                  className="flex-1 btn-secondary text-sm py-2"
                >
                  <Edit2 className="w-4 h-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => setShowQuotesModal(supplier)}
                  className="px-3 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition-colors"
                  title="Price Quotes"
                >
                  <FileText className="w-4 h-4" />
                  <span className="ml-1 text-xs">({supplier.price_quotes?.length || 0})</span>
                </button>
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}/vendor-360`)}
                  data-testid={`supplier-v360-${supplier.id}`}
                  className="px-3 py-2 bg-violet-100 text-violet-700 hover:bg-violet-200 rounded-lg transition-colors"
                  title="Vendor 360 Profile"
                >
                  <Award className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}/price-list`)}
                  data-testid={`supplier-pricelist-${supplier.id}`}
                  className="px-3 py-2 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg transition-colors"
                  title="Vendor Price List"
                >
                  <DollarSign className="w-4 h-4" />
                </button>
                {canDelete && (
                  <button
                    data-testid={`delete-supplier-${supplier.id}`}
                    onClick={() => handleDelete(supplier)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-gray-500">
            No suppliers found. Add your first supplier!
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingSupplier ? 'Edit Supplier' : 'New Supplier'}
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Name (Arabic) *</label>
                  <input
                    data-testid="supplier-name-arabic"
                    type="text"
                    value={formData.name_arabic}
                    onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
                    className="form-input"
                    dir="rtl"
                    placeholder="اسم المورد بالعربي"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Name (English) *</label>
                  <input
                    data-testid="supplier-name-english"
                    type="text"
                    value={formData.name_english}
                    onChange={(e) => setFormData({ ...formData, name_english: e.target.value })}
                    className="form-input"
                    placeholder="Supplier Name in English"
                    required
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    data-testid="supplier-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input"
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input"
                    placeholder="+966-XX-XXX-XXXX"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile</label>
                  <input
                    type="text"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className="form-input"
                    placeholder="+966-5X-XXX-XXXX"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="form-group mb-4">
                <label className="form-label">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="form-input"
                  rows="2"
                  placeholder="Full address"
                />
              </div>

              {/* Business Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">CR Number</label>
                  <input
                    type="text"
                    value={formData.cr_number}
                    onChange={(e) => setFormData({ ...formData, cr_number: e.target.value })}
                    className="form-input"
                    placeholder="Commercial Registration"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">VAT Number</label>
                  <input
                    type="text"
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    className="form-input"
                    placeholder="Tax Number"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="form-input"
                    placeholder="Contact Name"
                  />
                </div>
              </div>

              {/* Bank Details */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-3">Bank Details (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="form-input"
                      placeholder="Bank Name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                      className="form-input"
                      placeholder="Account Number"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">IBAN</label>
                    <input
                      type="text"
                      value={formData.bank_iban}
                      onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                      className="form-input"
                      placeholder="SA..."
                    />
                  </div>
                </div>
              </div>

              {/* Materials */}
              <div className="form-group mb-4">
                <label className="form-label">Materials (المواد)</label>
                <textarea
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="المواد التي يوفرها المورد - مثال: Fire Stop, Expansion Joint, Steel Doors..."
                  dir="auto"
                />
              </div>

              {/* Notes */}
              <div className="form-group mb-4">
                <label className="form-label">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="form-input"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-supplier-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingSupplier ? 'Update Supplier' : 'Create Supplier'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Price Quotes Modal */}
      {showQuotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="bg-orange-600 text-white p-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">Price Quotes - عروض الأسعار</h3>
                <p className="text-sm text-orange-100">{showQuotesModal.name_english}</p>
              </div>
              <button
                onClick={() => { setShowQuotesModal(null); setQuoteTitle(''); }}
                className="hover:bg-orange-700 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto max-h-[60vh]">
              {/* Upload New Quote */}
              <div className="bg-orange-50 rounded-xl p-4 mb-4 border border-orange-200">
                <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                  <Upload className="w-5 h-5" /> Upload New Quote
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Quote title (e.g., Fire Stop Prices 2026) - Required"
                    value={quoteTitle}
                    onChange={(e) => setQuoteTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <div className="flex gap-2">
                    <input
                      type="file"
                      id="quote-file-input"
                      accept=".pdf,application/pdf"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-600 file:text-white file:cursor-pointer"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          if (!quoteTitle.trim()) {
                            toast.error('Please enter a quote title first');
                            e.target.value = '';
                            return;
                          }
                          handleUploadQuote(showQuotesModal.id, e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                      disabled={uploadingQuote}
                    />
                  </div>
                  {uploadingQuote && (
                    <p className="text-orange-600 text-sm flex items-center gap-2">
                      <span className="animate-spin">⏳</span> Uploading...
                    </p>
                  )}
                </div>
              </div>

              {/* Quotes List */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">
                  Uploaded Quotes ({showQuotesModal.price_quotes?.length || 0})
                </h4>
                {showQuotesModal.price_quotes?.length > 0 ? (
                  <div className="space-y-2">
                    {showQuotesModal.price_quotes.map((quote, index) => (
                      <div 
                        key={quote.id || index} 
                        className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <FileText className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{quote.title}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(quote.uploaded_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${API.replace('/api', '')}${quote.file_url.startsWith('/api') ? quote.file_url : '/api' + quote.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            title="View PDF"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={`${API.replace('/api', '')}${quote.file_url.startsWith('/api') ? quote.file_url : '/api' + quote.file_url}`}
                            download
                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteQuote(showQuotesModal.id, quote.id)}
                            className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No price quotes uploaded yet</p>
                    <p className="text-sm">Upload your first quote above</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
